import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatPrice, BACKEND_URL } from "@/lib/api";
import { LogOut, Plus, Pencil, Trash2, Mail, ExternalLink, Copy } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminListingSheet } from "@/components/AdminListingSheet";
import { AdminPromoCodes } from "@/components/AdminPromoCodes";
import { PricelabsSyncBar } from "@/components/PricelabsSyncBar";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [bk, ls] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/listings"),
      ]);
      setBookings(bk.data || []);
      setListings(ls.data || []);
    } catch {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    const init = async () => {
      try {
        const m = await api.get("/auth/me");
        setMe(m.data);
        await load();
      } catch {
        navigate("/admin/login");
      }
    };
    init();
  }, [navigate, load]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("logout request failed", err);
      }
    }
    sessionStorage.removeItem("staytlv_admin_user");
    localStorage.removeItem("staytlv_admin_token");  // legacy cleanup
    localStorage.removeItem("staytlv_admin_user");   // legacy cleanup
    navigate("/admin/login");
  };

  const editListing = (l) => { setEditing(l); setSheetOpen(true); };
  const newListing = () => { setEditing(null); setSheetOpen(true); };
  const deleteListing = async (l) => {
    if (!window.confirm(`Delete ${l.name_en}?`)) return;
    try { await api.delete(`/admin/listings/${l.listing_id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const captureDeposit = async (b) => {
    try { await api.post(`/admin/bookings/${b.booking_id}/deposit/capture`); toast.success("Deposit captured"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Capture failed"); }
  };
  const releaseDeposit = async (b) => {
    try { await api.post(`/admin/bookings/${b.booking_id}/deposit/release`); toast.success("Deposit released"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
  };
  const runReviewEmails = async () => {
    try { const r = await api.post("/admin/run-post-stay-emails"); toast.success(t("admin.review_emails_sent", { n: r.data.sent })); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copyIcal = async (l) => {
    const url = `${BACKEND_URL}/api/listings/${l.listing_id}/ical`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        throw new Error("no clipboard api");
      }
      toast.success("iCal URL copied");
    } catch {
      // Fallback for iframe / non-secure contexts
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("iCal URL copied");
      } catch {
        // Last resort: show the URL so the user can copy manually
        toast.message("Copy iCal URL", { description: url, duration: 8000 });
      }
    }
  };

  if (!me) return <div className="py-32 text-center text-[#8C7A6B]">Loading…</div>;
  const lang = i18n.language;
  const revenue = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.total_due_now || 0), 0);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="overline mb-2">{t("admin.dashboard")}</p>
          <h1 className="font-serif text-4xl text-[#2C2A29]">Hello, {me.name || "Host"}</h1>
          <p className="text-[#8C7A6B] mt-1 text-sm">{me.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn"
                className="text-sm text-[#8C7A6B] hover:text-[#C86D53] inline-flex items-center gap-2">
          <LogOut className="h-4 w-4" /> {t("admin.logout")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        <Stat label={t("admin.bookings")} value={bookings.length} />
        <Stat label={t("admin.listings")} value={listings.length} />
        <Stat label={t("admin.revenue")} value={formatPrice(revenue, "USD")} />
      </div>

      <Tabs defaultValue="bookings">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="bookings" data-testid="tab-bookings">{t("admin.bookings")}</TabsTrigger>
          <TabsTrigger value="listings" data-testid="tab-listings">{t("admin.listings")}</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">{t("admin.promo_codes")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">{t("admin.settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <div className="flex justify-end mb-3">
            <button onClick={runReviewEmails} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="send-review-emails-btn">
              <Mail className="h-4 w-4" /> {t("admin.run_review_emails")}
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-[#EAE3D9] overflow-hidden">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead className="bg-[#F4EFE6] text-[#8C7A6B] uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-start px-5 py-3">{t("admin.guest")}</th>
                  <th className="text-start px-5 py-3">{t("admin.listings")}</th>
                  <th className="text-start px-5 py-3">{t("admin.dates")}</th>
                  <th className="text-start px-5 py-3">{t("admin.status")}</th>
                  <th className="text-start px-5 py-3">{t("admin.deposit")}</th>
                  <th className="text-end px-5 py-3">{t("admin.total")}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  const listing = listings.find((l) => l.listing_id === b.listing_id);
                  return (
                    <tr key={b.booking_id} className="border-t border-[#EAE3D9]" data-testid={`booking-row-${b.booking_id}`}>
                      <td className="px-5 py-4 text-[#2C2A29]">{b.guest?.first_name} {b.guest?.last_name}<div className="text-xs text-[#8C7A6B]">{b.guest?.email}</div></td>
                      <td className="px-5 py-4 text-[#595450]">{listing ? (lang === "he" ? listing.name_he : listing.name_en) : b.listing_id}</td>
                      <td className="px-5 py-4 text-[#595450]">{b.check_in} → {b.check_out}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full ${b.payment_status === "paid" ? "bg-[#8A9A86]/20 text-[#3F5036]" : "bg-[#F4EFE6] text-[#8C7A6B]"}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs">
                        <span className="text-[#8C7A6B]">{b.deposit_status || "—"}</span>
                        {b.deposit_status === "authorized" && (
                          <div className="flex gap-2 mt-1">
                            <button onClick={() => captureDeposit(b)} data-testid={`capture-deposit-${b.booking_id}`} className="text-[10px] underline text-[#C86D53]">{t("admin.capture_deposit")}</button>
                            <button onClick={() => releaseDeposit(b)} data-testid={`release-deposit-${b.booking_id}`} className="text-[10px] underline text-[#8C7A6B]">{t("admin.release_deposit")}</button>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-end font-medium text-[#2C2A29]">{formatPrice(b.total_due_now, b.currency)}</td>
                    </tr>
                  );
                })}
                {bookings.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#8C7A6B]">No bookings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="listings">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <PricelabsSyncBar listings={listings} onSynced={load} />
            <button onClick={newListing} className="btn-primary text-sm inline-flex items-center gap-2 ms-auto" data-testid="new-listing-btn">
              <Plus className="h-4 w-4" /> {t("admin.add_listing")}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((l) => (
              <div key={l.listing_id} className="bg-white rounded-2xl border border-[#EAE3D9] overflow-hidden" data-testid={`admin-listing-${l.listing_id}`}>
                <img src={l.cover_image} alt="" className="w-full h-44 object-cover" />
                <div className="p-5">
                  <div className="text-xs text-[#8C7A6B]">{lang === "he" ? l.neighborhood_he : l.neighborhood_en}</div>
                  <div className="font-serif text-xl text-[#2C2A29] mt-1">{lang === "he" ? l.name_he : l.name_en}</div>
                  <div className="flex items-baseline justify-between mt-3">
                    <span className="text-[#595450] text-sm">{formatPrice(l.price_per_night, l.currency)} {t("listings.per_night")}</span>
                    <span className="text-xs text-[#8C7A6B]">{l.bedrooms} {t("listings.bedrooms")}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#EAE3D9] text-xs">
                    <button onClick={() => editListing(l)} className="text-[#C86D53] inline-flex items-center gap-1 hover:underline" data-testid={`edit-listing-${l.listing_id}`}>
                      <Pencil className="h-3 w-3" /> {t("admin.edit")}
                    </button>
                    <button onClick={() => copyIcal(l)} className="text-[#8C7A6B] inline-flex items-center gap-1 hover:text-[#C86D53]" data-testid={`copy-ical-${l.listing_id}`}>
                      <Copy className="h-3 w-3" /> iCal
                    </button>
                    <a href={`/listings/${l.slug}`} target="_blank" rel="noreferrer" className="text-[#8C7A6B] inline-flex items-center gap-1 hover:text-[#C86D53]">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <button onClick={() => deleteListing(l)} className="text-[#8C7A6B] inline-flex items-center gap-1 hover:text-[#C86D53] ms-auto" data-testid={`delete-listing-${l.listing_id}`}>
                      <Trash2 className="h-3 w-3" /> {t("admin.delete")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="promos">
          <AdminPromoCodes />
        </TabsContent>

        <TabsContent value="settings">
          <div className="bg-white rounded-2xl border border-[#EAE3D9] p-8 max-w-2xl mt-4" data-testid="settings-panel">
            <h2 className="font-serif text-2xl text-[#2C2A29]">{t("admin.resend_domain_title")}</h2>
            <p className="text-sm text-[#595450] mt-2 leading-relaxed">{t("admin.resend_domain_body")}</p>
            <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center gap-2 mt-5 text-sm">
              <ExternalLink className="h-4 w-4" /> resend.com/domains
            </a>
            <div className="mt-10 pt-8 border-t border-[#EAE3D9]">
              <h3 className="font-serif text-xl text-[#2C2A29]">{t("admin.ical_import")}</h3>
              <p className="text-sm text-[#595450] mt-2">
                Each listing has its own <code className="text-[#C86D53]">{`{BACKEND_URL}/api/listings/{id}/ical`}</code> export feed (click &ldquo;iCal&rdquo; on a stay to copy). Paste external Airbnb / Booking.com calendar URLs in a listing&rsquo;s edit form to import their blocked dates automatically.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <AdminListingSheet
        key={editing?.listing_id || "new"}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        listing={editing}
        onSaved={load}
      />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-2xl p-6 border border-[#EAE3D9]">
    <div className="text-xs uppercase tracking-wider text-[#8C7A6B]">{label}</div>
    <div className="font-serif text-3xl text-[#2C2A29] mt-2">{value}</div>
  </div>
);
